export interface PixelSize {
  width: number;
  height: number;
}

/**
 * One source pixel per device pixel. React Native Web draws `Image` as a CSS
 * background, and `background-size: contain` scales the bitmap to the element
 * box, so the box has to be this size or the viewer enlarges the file.
 */
function sharpPixelSize(
  natural: PixelSize,
  pixelRatio: number,
): PixelSize | null {
  const ratio = pixelRatio > 0 ? pixelRatio : 1;
  const width = natural.width / ratio;
  const height = natural.height / ratio;
  if (width <= 0 || height <= 0) {
    return null;
  }
  return { width, height };
}

function contain(
  sharp: PixelSize,
  pane: PixelSize,
  capAtOne: boolean,
): PixelSize {
  if (pane.width <= 0 || pane.height <= 0) {
    return { width: 0, height: 0 };
  }
  const fit = Math.min(pane.width / sharp.width, pane.height / sharp.height);
  const scale = capAtOne ? Math.min(1, fit) : fit;
  return {
    width: Math.max(1, Math.round(sharp.width * scale)),
    height: Math.max(1, Math.round(sharp.height * scale)),
  };
}

/**
 * Actual pixels, then shrink only when that size is larger than the pane.
 */
export function sharpContainSize(
  natural: PixelSize,
  pane: PixelSize,
  pixelRatio: number,
): PixelSize {
  const sharp = sharpPixelSize(natural, pixelRatio);
  if (!sharp) {
    return { width: 0, height: 0 };
  }
  return contain(sharp, pane, true);
}

/**
 * The size on open. The picture grows until it touches the pane, so a short
 * wide screenshot is as wide as the viewer. A picture already larger than the
 * pane only shrinks, matching `sharpContainSize`.
 */
export function paneContainSize(
  natural: PixelSize,
  pane: PixelSize,
  pixelRatio: number,
): PixelSize {
  const sharp = sharpPixelSize(natural, pixelRatio);
  if (!sharp) {
    return { width: 0, height: 0 };
  }
  return contain(sharp, pane, false);
}

/**
 * Below this, actual pixels are a clear step down from the fitted size, so
 * double-tap lands on them. At or above it the step is a twitch, and
 * double-tap zooms instead.
 */
const SHARP_STEP_RATIO = 0.8;

/** The zoom the viewer used before sizing was measured. */
const DOUBLE_TAP_ZOOM = 2.5;

/**
 * The size after a double-tap. A short screenshot drops to actual pixels.
 * A picture already near the fitted size grows to 2.5× that size; the viewer
 * clips it to the middle. There is no pan.
 */
export function doubleTapSize(
  natural: PixelSize,
  pane: PixelSize,
  pixelRatio: number,
): PixelSize {
  const fitted = paneContainSize(natural, pane, pixelRatio);
  if (fitted.width <= 0 || fitted.height <= 0) {
    return { width: 0, height: 0 };
  }
  const sharp = sharpContainSize(natural, pane, pixelRatio);
  if (sharp.width / fitted.width < SHARP_STEP_RATIO) {
    return sharp;
  }
  return {
    width: Math.round(fitted.width * DOUBLE_TAP_ZOOM),
    height: Math.round(fitted.height * DOUBLE_TAP_ZOOM),
  };
}
